import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, CalendarDays, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import F24Modal from "@/components/F24Modal";

const CATS = [
  { v: "fisco", l: "Fisco" },
  { v: "scuola", l: "Scuola" },
  { v: "casa", l: "Casa" },
  { v: "veicoli", l: "Veicoli" },
  { v: "altro", l: "Altro" },
];

// Categorie per cui è utile mostrare il pulsante "Prepara F24"
const F24_CATEGORIES = new Set(["IRPEF", "IVA", "INPS", "IMU", "F24", "Redditi", "Forfettario"]);
const isF24Relevant = (cat) => F24_CATEGORIES.has(cat) || F24_CATEGORIES.has((cat || "").toUpperCase());

const catColor = {
  fisco: "bg-primary/10 text-primary",
  scuola: "bg-secondary/10 text-secondary",
  casa: "bg-accent text-accent-foreground",
  veicoli: "bg-primary/10 text-primary",
  altro: "bg-muted text-muted-foreground",
};

export default function DeadlinesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [date, setDate] = useState("");
  const [cat, setCat] = useState("fisco");
  const [f24Deadline, setF24Deadline] = useState(null);

  const load = () => api.get("/deadlines").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title || !date) { toast.error("Titolo e data obbligatori"); return; }
    try {
      await api.post("/deadlines", { title, description: desc, due_date: date, category: cat });
      toast.success("Scadenza aggiunta");
      setOpen(false); setTitle(""); setDesc(""); setDate(""); setCat("fisco");
      load();
    } catch { toast.error("Errore"); }
  };

  const toggle = async (id) => { await api.patch(`/deadlines/${id}`); load(); };
  const del = async (id) => { await api.delete(`/deadlines/${id}`); load(); toast.success("Rimossa"); };

  return (
    <div className="space-y-6" data-testid="deadlines-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Scadenzario</div>
          <h1 className="font-heading font-bold text-4xl tracking-tighter">Le tue prossime scadenze.</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-12 px-6" data-testid="add-deadline-button"><Plus className="w-4 h-4 mr-2" /> Aggiungi scadenza</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Nuova scadenza</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titolo</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-12 rounded-xl mt-1.5" placeholder="Es. F24 acconto IRPEF" data-testid="deadline-title-input" /></div>
              <div><Label>Descrizione</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="rounded-xl mt-1.5" data-testid="deadline-desc-input" /></div>
              <div><Label>Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12 rounded-xl mt-1.5" data-testid="deadline-date-input" /></div>
              <div><Label>Categoria</Label>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger className="h-12 rounded-xl mt-1.5" data-testid="deadline-cat-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={create} className="rounded-full w-full h-12" data-testid="deadline-save-button">Salva</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 rounded-2xl border-dashed border-2 text-center" data-testid="deadlines-empty">
          <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading font-semibold text-xl mb-1">Nessuna scadenza</div>
          <div className="text-muted-foreground text-sm">Aggiungi la tua prima scadenza per iniziare.</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((d) => {
            const isPast = new Date(d.due_date) < new Date(new Date().toDateString());
            // Fix R2: scadenze condizionali (CU "solo se…", IMU, etc.) non vanno mai marcate "in ritardo".
            // Le rileviamo dal testo della descrizione che tipicamente contiene "solo se" o dalla categoria.
            const desc = ((d.description || "") + " " + (d.title || "")).toLowerCase();
            const isConditional = desc.includes("solo se") || desc.includes("se hai") || desc.includes("eventual") || d.category === "CU";
            const isOverdue = isPast && !d.completed && !d.pre_registration && !isConditional;
            const isPreReg = d.pre_registration && !d.completed;
            const isVerificaNeeded = isPast && !d.completed && !d.pre_registration && isConditional;
            return (
            <Card key={d.id} className={`p-5 rounded-2xl border flex items-start gap-4 ${d.completed ? "opacity-60" : isPreReg ? "opacity-70 bg-muted/30" : isOverdue ? "border-secondary/40 bg-secondary/5" : ""}`} data-testid={`deadline-item-${d.id}`}>
              <Checkbox checked={d.completed} onCheckedChange={() => toggle(d.id)} className="mt-1" data-testid={`deadline-check-${d.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`font-semibold ${d.completed ? "line-through" : ""}`}>{d.title}</div>
                  <Badge variant="secondary" className={`rounded-full ${catColor[d.category] || ""}`}>{d.category}</Badge>
                  {isPreReg && (
                    <Badge variant="outline" className="rounded-full text-xs bg-muted/50 text-muted-foreground border-muted-foreground/30" data-testid={`deadline-prereg-${d.id}`}>
                      Non verificata
                    </Badge>
                  )}
                  {isVerificaNeeded && (
                    <Badge variant="outline" className="rounded-full text-xs bg-muted/50 text-muted-foreground border-muted-foreground/30" data-testid={`deadline-verifica-${d.id}`}>
                      Da verificare
                    </Badge>
                  )}
                  {isOverdue && (
                    <Badge variant="outline" className="rounded-full text-xs bg-secondary/10 text-secondary border-secondary/30">
                      In ritardo
                    </Badge>
                  )}
                </div>
                {d.description && <div className="text-sm text-muted-foreground mt-1">{d.description}</div>}
                {isPreReg && (
                  <div className="text-xs text-muted-foreground italic mt-1">
                    Scadenza precedente alla tua registrazione. Verifica se era già pagata (es. da commercialista) prima di segnarla come completata.
                  </div>
                )}
                <div className={`text-xs font-semibold mt-2 ${isPreReg ? "text-muted-foreground" : "text-primary"}`}>
                  Scadenza: {new Date(d.due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                </div>
                {isF24Relevant(d.category) && !d.completed && !isPreReg && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setF24Deadline(d)}
                    className="rounded-full h-8 mt-3 text-xs"
                    data-testid={`deadline-f24-${d.id}`}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> Prepara F24
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(d.id)} data-testid={`deadline-del-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
            </Card>
            );
          })}
        </div>
      )}

      {/* F24 pre-compilation modal */}
      <F24Modal
        open={!!f24Deadline}
        onOpenChange={(v) => !v && setF24Deadline(null)}
        deadline={f24Deadline}
      />
    </div>
  );
}
